class CvHeader extends HTMLElement {
	connectedCallback() {
		this.abort = new AbortController();
		this.desktopRelocateDropdowns();
		this.desktopSetupNav();
		this.drawerSetup();
		this.stickySetup();
	}

	disconnectedCallback() {
		this.abort.abort();
		if (this.drawerIsOpen()) this.drawerClose();
	}

	// ── Desktop nav ──

	desktopRelocateDropdowns() {
		const section = this.querySelector(".cv-section");
		if (!section) return;
		this.querySelectorAll(".cv-header__dropdown").forEach((panel) => {
			section.appendChild(panel);
		});
	}

	desktopSetupNav() {
		this.desktopTriggers = this.querySelectorAll(
			".cv-header__nav-trigger[aria-controls]"
		);
		this.desktopTriggerMode = this.dataset.triggerMode || "hover";

		this.desktopTriggers.forEach((trigger) => {
			const item = trigger.closest(".cv-header__nav-section");
			const panel = this.desktopPanelFor(trigger);
			if (!item || !panel) return;

			trigger.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					this.desktopToggleDropdown(trigger, panel);
				}
				if (e.key === "Escape") {
					this.desktopCloseAllDropdowns();
					trigger.focus();
				}
			});

			trigger.addEventListener("click", (e) => {
				e.preventDefault();
				this.desktopToggleDropdown(trigger, panel);
			});

			if (this.desktopTriggerMode === "hover") {
				let timeout;
				const open = () => {
					clearTimeout(timeout);
					this.desktopCloseAllDropdowns();
					this.desktopOpenDropdown(trigger, panel);
				};
				const startClose = () => {
					timeout = setTimeout(
						() => this.desktopCloseDropdown(trigger, panel),
						300
					);
				};

				item.addEventListener("mouseenter", open);
				item.addEventListener("mouseleave", startClose);
				panel.addEventListener("mouseenter", () => clearTimeout(timeout));
				panel.addEventListener("mouseleave", startClose);
			}
		});

		document.addEventListener(
			"click",
			(e) => {
				if (!this.contains(e.target)) this.desktopCloseAllDropdowns();
			},
			{ signal: this.abort.signal }
		);
	}

	desktopPanelFor(trigger) {
		const id = trigger.getAttribute("aria-controls");
		return id ? document.getElementById(id) : null;
	}

	desktopToggleDropdown(trigger, panel) {
		const isOpen = panel.getAttribute("aria-hidden") === "false";
		this.desktopCloseAllDropdowns();
		if (!isOpen) this.desktopOpenDropdown(trigger, panel);
	}

	desktopOpenDropdown(trigger, panel) {
		this.setDisclosureState(trigger, panel, true);
	}

	desktopCloseDropdown(trigger, panel) {
		this.setDisclosureState(trigger, panel, false);
	}

	desktopCloseAllDropdowns() {
		this.desktopTriggers.forEach((trigger) => {
			const panel = this.desktopPanelFor(trigger);
			if (panel) this.desktopCloseDropdown(trigger, panel);
		});
	}

	// ── Mobile drawer ──

	drawerSetup() {
		this.drawerEl = this.querySelector(".cv-header__drawer");
		this.drawerBackdrop = this.querySelector(".cv-header__drawer-backdrop");
		this.drawerHamburger = this.querySelector(".cv-header__hamburger");
		const closeBtn = this.querySelector(".cv-header__drawer-close");

		if (!this.drawerEl || !this.drawerHamburger) return;

		this.drawerPopulate();

		this.drawerHamburger.addEventListener("click", () => this.drawerOpen());
		if (closeBtn) closeBtn.addEventListener("click", () => this.drawerClose());
		if (this.drawerBackdrop)
			this.drawerBackdrop.addEventListener("click", () => this.drawerClose());

		document.addEventListener(
			"keydown",
			(e) => {
				if (e.key === "Escape" && this.drawerIsOpen()) this.drawerClose();
			},
			{ signal: this.abort.signal }
		);
	}

	drawerPopulate() {
		const list = this.drawerEl.querySelector(".cv-header__drawer-sections");
		if (!list) return;

		list.replaceChildren();

		this.querySelectorAll(".cv-header__nav-section").forEach((section) => {
			const trigger = section.querySelector(".cv-header__nav-trigger");
			if (!trigger) return;

			const title = trigger.textContent.trim();
			const panel = this.desktopPanelFor(trigger);
			const li = document.createElement("li");

			if (panel) {
				li.appendChild(this.drawerBuildDisclosure(title, panel));
			} else {
				const a = document.createElement("a");
				a.href = (trigger.closest("a") || trigger).href || "#";
				a.textContent = title;
				li.appendChild(a);
			}

			list.appendChild(li);
		});

		list.addEventListener(
			"toggle",
			(e) => {
				if (!e.target.open) return;
				list.querySelectorAll("details[open]").forEach((d) => {
					if (d !== e.target) d.open = false;
				});
			},
			true
		);
	}

	drawerBuildDisclosure(title, panel) {
		const details = document.createElement("details");
		const summary = document.createElement("summary");
		summary.textContent = title;
		details.appendChild(summary);

		const content = document.createElement("div");
		content.className = "cv-header__drawer-content";

		const inner = panel.querySelector(".cv-header__dropdown-inner");
		if (inner) {
			const clone = inner.cloneNode(true);
			const columns = document.createElement("div");
			columns.className = "cv-header__drawer-columns";
			const promos = document.createElement("div");
			promos.className = "cv-header__drawer-promos";

			const stack = clone.querySelector(".cv-stack");
			if (stack) {
				Array.from(stack.children).forEach((child) => {
					if (child.classList.contains("cv-header__nav-promo")) {
						promos.appendChild(child);
					} else {
						columns.appendChild(child);
					}
				});
			}

			if (columns.children.length) content.appendChild(columns);
			if (promos.children.length) content.appendChild(promos);
		}

		details.appendChild(content);
		return details;
	}

	drawerIsOpen() {
		return this.drawerEl?.getAttribute("aria-hidden") === "false";
	}

	drawerOpen() {
		this.setDisclosureState(this.drawerHamburger, this.drawerEl, true);
	}

	drawerClose() {
		this.setDisclosureState(this.drawerHamburger, this.drawerEl, false);
	}

	// ── Sticky ──

	stickySetup() {
		const stickyType = this.dataset.stickyType;
		if (stickyType === "none") return;

		const stickyWrapper = this.closest(".cv-header-section");
		if (!stickyWrapper) return;

		stickyWrapper.classList.add("cv-header-section--sticky");
		if (stickyType === "always") return;

		let lastScrollTop = 0;
		stickyWrapper.style.setProperty(
			"--cv-header-height",
			`${this.offsetHeight}px`
		);
		window.addEventListener(
			"scroll",
			() => {
				const scrollTop = window.scrollY;
				if (scrollTop > lastScrollTop && scrollTop > this.offsetHeight) {
					stickyWrapper.classList.add("cv-header-section--hidden");
				} else {
					stickyWrapper.classList.remove("cv-header-section--hidden");
				}
				lastScrollTop = scrollTop;
			},
			{ passive: true, signal: this.abort.signal }
		);
	}

	// ── Helpers ──

	setDisclosureState(trigger, panel, isOpen) {
		trigger.setAttribute("aria-expanded", String(isOpen));
		panel.setAttribute("aria-hidden", String(!isOpen));
	}
}

if (!customElements.get("cv-header")) {
	customElements.define("cv-header", CvHeader);
}
